import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  RefreshCcw,
  Link2,
  Unlink,
  CheckCircle,
  AlertCircle,
  Clock,
  Loader2,
  Copy,
  Info,
  UserPlus,
  Users,
  Key
} from 'lucide-react';
import {
  Alert,
  AlertDescription
} from '@/components/ui/alert';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import { cn } from '@/lib/utils';
import {
  LW_DS_CLASS,
  LwButton,
  LwModal,
  LwStatusPill
} from '@/components/lw';

const log = createComponentLogger('EncompassCredentials', 'integration');

interface EncompassUser {
  id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  loginEnabled?: boolean;
  accountEnabled?: boolean;
  personas?: Array<{ entityId: string; entityName: string }>;
  personaNames?: string;
}

interface EncompassOrganization {
  id: string;
  name: string;
  path: string;
  parentId?: string | null;
}

interface EncompassPersona {
  id: string;
  name: string;
}

type EncompassLinkState =
  | 'not_linked'
  | 'linked'
  | 'missing'
  | 'verification_unavailable';

type EncompassSyncStatus = 'not_synced' | 'synced' | 'error' | 'pending';

interface EncompassStatusResponse {
  linkState?: EncompassLinkState;
  encompassId: string | null;
  syncStatus: EncompassSyncStatus;
  syncError: string | null;
  syncedAt: string | null;
  verificationMessage?: string;
}

interface EncompassCredentialsProps {
  userId: string;
  userEmail: string;
  firstName?: string;
  lastName?: string;
  isAdminView?: boolean;
  presentation?: 'default' | 'profile';
}

export const EncompassCredentials: React.FC<EncompassCredentialsProps> = ({
  userId,
  userEmail,
  firstName,
  lastName,
  isAdminView = false,
  presentation = 'default'
}) => {
  const isProfilePresentation = presentation === 'profile';
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searching, setSearching] = useState(false);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [unlinkDialogOpen, setUnlinkDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);

  // Search state
  const [searchResults, setSearchResults] = useState<EncompassUser[]>([]);
  const [syncedUserCount, setSyncedUserCount] = useState(0);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [connectionMethod, setConnectionMethod] = useState<'search' | 'create'>('search');

  // Organization and Persona state
  const [organizations, setOrganizations] = useState<EncompassOrganization[]>([]);
  const [personas, setPersonas] = useState<EncompassPersona[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [selectedPersonaId, setSelectedPersonaId] = useState<string>('7'); // Default: Loan Officer
  const [loadingSettings, setLoadingSettings] = useState(false);

  // Encompass sync state
  const [encompassUserId, setEncompassUserId] = useState<string | null>(null);
  const [linkState, setLinkState] = useState<EncompassLinkState>('not_linked');
  const [syncStatus, setSyncStatus] = useState<EncompassSyncStatus>('not_synced');
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncedAt, setSyncedAt] = useState<Date | null>(null);
  const [temporaryPassword, setTemporaryPassword] = useState<string | null>(null);
  const [verificationMessage, setVerificationMessage] = useState<string | null>(null);

  const loadEncompassStatus = useCallback(async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('users')
        .select('encompass_user_id, encompass_sync_status, encompass_sync_error, encompass_synced_at')
        .eq('id', userId)
        .single();

      if (error) {
        log.error('Failed to load Encompass status', { userId }, error);
        return;
      }

      if (data) {
        const localEncompassId = data.encompass_user_id;
        setEncompassUserId(localEncompassId);
        setSyncStatus(data.encompass_sync_status || 'not_synced');
        setSyncError(data.encompass_sync_error);
        setSyncedAt(data.encompass_synced_at ? new Date(data.encompass_synced_at) : null);

        if (!localEncompassId) {
          setLinkState('not_linked');
          setVerificationMessage(null);
          return;
        }

        setLinkState('verification_unavailable');
        setVerificationMessage('Checking the saved Encompass link...');

        const { data: session } = await supabase.auth.getSession();
        if (!session?.session?.access_token) {
          setVerificationMessage('The saved Encompass link could not be verified because your session expired.');
          return;
        }

        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/encompass-sync`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session.session.access_token}`
            },
            body: JSON.stringify({
              userId,
              action: 'status'
            })
          }
        );
        const result = await response.json() as EncompassStatusResponse & { error?: string };

        if (!response.ok) {
          throw new Error(result.error || 'Failed to verify Encompass link');
        }

        setEncompassUserId(result.encompassId);
        setLinkState(result.linkState || (result.encompassId ? 'linked' : 'not_linked'));
        setSyncStatus(result.syncStatus);
        setSyncError(result.syncError);
        setSyncedAt(result.syncedAt ? new Date(result.syncedAt) : null);
        setVerificationMessage(result.verificationMessage || null);
      }
    } catch (error) {
      log.error('Error loading Encompass status', { userId }, error as Error);
      setLinkState((current) => current === 'not_linked' ? current : 'verification_unavailable');
      setVerificationMessage('The saved Encompass link could not be verified right now.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Load Encompass sync status
  useEffect(() => {
    loadEncompassStatus();
  }, [loadEncompassStatus]);

  const loadAllEncompassUsers = async () => {
    try {
      setSearching(true);
      setSearchResults([]);

      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('Session expired. Please refresh the page.');
        setSearching(false);
        return;
      }

      // Call edge function to get REAL unsynced Encompass users from API
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/encompass-sync`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            action: 'search',
            userId // Required for auth verification
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to load Encompass users');
      }

      const result = await response.json();

      // result.users contains REAL unsynced Encompass users from API
      const realUsers: EncompassUser[] = result.users || [];
      setSearchResults(realUsers);
      setSyncedUserCount(result.synced || 0);

      if (realUsers.length === 0) {
        toast.info(`All ${result.synced || 0} Encompass users are already synced. You can create a new Encompass user for this profile.`);
        setConnectionMethod('create');
      } else {
        toast.success(`Found ${result.available} available Encompass user(s) (${result.synced} already synced)`);
        setConnectionMethod('search');
      }
    } catch (error) {
      log.error('Failed to load Encompass users', { userId }, error as Error);
      toast.error('Failed to load Encompass users. You can still create a new user.');
      setSearchResults([]);
      setConnectionMethod('create');
    } finally {
      setSearching(false);
    }
  };

  const loadEncompassSettings = async () => {
    try {
      setLoadingSettings(true);

      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('Session expired. Please refresh the page.');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/encompass-sync`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            action: 'get-settings',
            userId
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to load Encompass settings');
      }

      const result = await response.json();

      setOrganizations(result.organizations || []);
      setPersonas(result.personas || []);

      // Set defaults
      if (result.defaults?.organizationId) {
        setSelectedOrgId(result.defaults.organizationId);
      }
      if (result.defaults?.personaId) {
        setSelectedPersonaId(result.defaults.personaId);
      }

      log.info('Loaded Encompass settings', {
        organizations: result.organizations?.length,
        personas: result.personas?.length,
        defaultOrgId: result.defaults?.organizationId
      });
    } catch (error) {
      log.error('Failed to load Encompass settings', { userId }, error as Error);
      toast.error('Failed to load organization settings');
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleConnect = async () => {
    if (connectionMethod === 'search' && !selectedUserId && searchResults.length > 0) {
      toast.error('Please select an Encompass user to link');
      return;
    }

    try {
      setSyncing(true);
      setSyncError(null);
      setTemporaryPassword(null);

      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('Session expired. Please refresh the page.');
        return;
      }

      if (connectionMethod === 'search' && selectedUserId) {
        // Link to existing user
        toast.info(`Linking to Encompass user: ${selectedUserId}...`);

        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/encompass-sync`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.session.access_token}`
          },
          body: JSON.stringify({
            userId,
            action: 'link',
            encompassId: selectedUserId
          })
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || 'Failed to link user');
        }

        toast.success('Successfully linked to Encompass user!');
        setEncompassUserId(selectedUserId);
        setLinkState('linked');
        setSyncStatus('synced');
        setSyncError(null);
        setVerificationMessage(null);
        setSyncedAt(new Date());
        setConnectDialogOpen(false);

      } else {
        // Create new user or auto-link
        toast.info('Connecting to Encompass...');

        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/encompass-sync`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.session.access_token}`
          },
          body: JSON.stringify({
            userId,
            action: 'sync',
            organizationId: selectedOrgId || undefined,
            personaId: selectedPersonaId || undefined
          })
        });

        const result = await response.json();

        if (response.status === 409) {
          toast.error(result.error || 'An Encompass user with this email already exists. Link it explicitly or disable it before creating a new one.');
          return;
        }

        if (!response.ok) {
          throw new Error(result.error || 'Failed to sync with Encompass');
        }

        if (result.status === 'created') {
          toast.success('Successfully created Encompass user!');
          setEncompassUserId(result.encompassId);
          setLinkState('linked');
          setTemporaryPassword(result.password);
          setSyncStatus('synced');
          setSyncError(null);
          setVerificationMessage(null);
          setSyncedAt(new Date());

          if (result.password) {
            toast.info(`Temporary password: ${result.password}`, {
              duration: 10000,
              description: 'User must change password on first login'
            });
          }
        } else if (result.status === 'linked') {
          toast.success('Successfully linked to existing Encompass user!');
          setEncompassUserId(result.encompassId);
          setLinkState('linked');
          setSyncStatus('synced');
          setSyncError(null);
          setVerificationMessage(null);
          setSyncedAt(new Date());
        } else if (result.status === 'updated') {
          toast.success('Encompass user updated successfully!');
          setLinkState('linked');
          setSyncStatus('synced');
          setSyncError(null);
          setVerificationMessage(null);
          setSyncedAt(new Date());
        }

        setConnectDialogOpen(false);
      }

      // Reload status
      await loadEncompassStatus();

    } catch (error) {
      log.error('Failed to connect to Encompass', { userId }, error as Error);
      const message = error instanceof Error ? error.message : 'Failed to connect to Encompass';
      toast.error(message);
      setSyncError(message);
      setSyncStatus('error');
    } finally {
      setSyncing(false);
    }
  };

  const handleUnlink = async () => {
    try {
      setSyncing(true);

      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('Session expired. Please refresh the page.');
        return;
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/encompass-sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.session.access_token}`
        },
        body: JSON.stringify({
          userId,
          action: 'unlink'
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to unlink user');
      }

      toast.success('Successfully unlinked Encompass user');
      if (result.remoteAccountUpdated) {
        toast.success('Disabled and detached the old Encompass account');
      } else if (result.remoteAccountMissing) {
        toast.success('Removed the stale link to the missing Encompass user');
      }
      setEncompassUserId(null);
      setLinkState('not_linked');
      setSyncStatus('not_synced');
      setSyncError(null);
      setSyncedAt(null);
      setVerificationMessage(null);
      setUnlinkDialogOpen(false);

    } catch (error) {
      log.error('Failed to unlink Encompass user', { userId }, error as Error);
      const message = error instanceof Error ? error.message : 'Failed to unlink user';
      toast.error(message);
    } finally {
      setSyncing(false);
    }
  };

  const handleResetPassword = async () => {
    try {
      setSyncing(true);
      setTemporaryPassword(null);

      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('Session expired. Please refresh the page.');
        return;
      }

      toast.info('Resetting Encompass password...');

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/encompass-sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.session.access_token}`
        },
        body: JSON.stringify({
          userId,
          action: 'reset-password'
        })
      });

      const result = await response.json();

      if (!response.ok) {
        if (result.code === 'ENCOMPASS_USER_MISSING') {
          setLinkState('missing');
          setSyncStatus('error');
          setSyncError(result.error);
          setVerificationMessage(null);
          setResetPasswordDialogOpen(false);
        }
        throw new Error(result.error || 'Failed to reset password');
      }

      toast.success('Password reset successfully!', {
        duration: 8000,
        description: 'Copy the new password and use it to log into Encompass'
      });

      setTemporaryPassword(result.password);
      setResetPasswordDialogOpen(false);

    } catch (error) {
      log.error('Failed to reset Encompass password', { userId }, error as Error);
      const message = error instanceof Error ? error.message : 'Failed to reset password';
      toast.error(message);
    } finally {
      setSyncing(false);
    }
  };

  const copyEncompassId = () => {
    if (encompassUserId) {
      navigator.clipboard.writeText(encompassUserId);
      toast.success('Encompass ID copied to clipboard');
    }
  };

  const copyTemporaryPassword = () => {
    if (temporaryPassword) {
      navigator.clipboard.writeText(temporaryPassword);
      toast.success('Password copied to clipboard');
    }
  };

  const getSyncStatusBadge = () => {
    if (linkState === 'missing') {
      return <Badge variant="outline" className={cn('border-warning/30 bg-warning/10 text-warning', isProfilePresentation && 'lw-credential-card__status')}>
        <AlertCircle className="w-3 h-3 mr-1" />
        Link Missing
      </Badge>;
    }

    if (linkState === 'verification_unavailable') {
      return <Badge variant="outline" className={cn('border-warning/30 bg-warning/10 text-warning', isProfilePresentation && 'lw-credential-card__status')}>
        <Clock className="w-3 h-3 mr-1" />
        Check Unavailable
      </Badge>;
    }

    switch (syncStatus) {
      case 'synced':
        return <Badge variant="outline" className={cn('bg-green-50 text-green-700 border-green-200', isProfilePresentation && 'lw-credential-card__status')}>
          <CheckCircle className="w-3 h-3 mr-1" />
          Connected
        </Badge>;
      case 'error':
        return <Badge variant="outline" className={cn('bg-red-50 text-red-700 border-red-200', isProfilePresentation && 'lw-credential-card__status')}>
          <AlertCircle className="w-3 h-3 mr-1" />
          Error
        </Badge>;
      case 'pending':
        return <Badge variant="outline" className={cn('bg-yellow-50 text-yellow-700 border-yellow-200', isProfilePresentation && 'lw-credential-card__status')}>
          <Clock className="w-3 h-3 mr-1" />
          Pending
        </Badge>;
      default:
        return <Badge variant="outline" className={cn('bg-gray-50 text-gray-700 border-gray-200', isProfilePresentation && 'lw-credential-card__status')}>
          <AlertCircle className="w-3 h-3 mr-1" />
          Not Connected
        </Badge>;
    }
  };

  const openConnectDialog = () => {
    setSelectedUserId('');
    setSearchResults([]);
    setSyncedUserCount(0);
    setOrganizations([]);
    setPersonas([]);
    setConnectDialogOpen(true);
    if (isAdminView) {
      setConnectionMethod('search');
      // Auto-load users and settings when dialog opens (admin only)
      setTimeout(() => {
        loadAllEncompassUsers();
        loadEncompassSettings();
      }, 100);
    } else {
      setConnectionMethod('create');
      // Self-service: only load settings (org/persona defaults), skip search
      setTimeout(() => {
        loadEncompassSettings();
      }, 100);
    }
  };

  if (loading) {
    return (
      <div className={cn('border rounded-lg p-6 flex items-center justify-center', isProfilePresentation && 'lw-credential-card lw-credential-card--loading')}>
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <div className={cn('border rounded-lg p-6 space-y-4', isProfilePresentation && 'lw-credential-card')} onClick={(e) => e.stopPropagation()}>
        <div className="lw-credential-card__header flex items-start justify-between">
          <div className="w-full space-y-1">
            <div className="flex items-center justify-between gap-3">
              <h3 className="lw-credential-card__title text-lg font-semibold flex items-center gap-2">
                {isProfilePresentation ? 'Encompass' : 'Encompass Integration'}
                {!isProfilePresentation && getSyncStatusBadge()}
              </h3>
              {isProfilePresentation && getSyncStatusBadge()}
            </div>
            <p className="lw-credential-card__note text-sm text-muted-foreground">
              {isProfilePresentation
                ? isAdminView
                  ? 'Admin controls · search, link, create, reset or unlink'
                  : 'Your connection · status, profile sync and password reset'
                : 'Connect this profile to Encompass LOS for seamless loan origination workflow.'}
            </p>
          </div>
        </div>

        {/* Status Display */}
        {encompassUserId && linkState === 'missing' ? (
          <div className="space-y-4">
            <Alert className="border-warning/40 bg-warning/10" role="status">
              <AlertCircle className="h-4 w-4 text-warning" aria-hidden="true" />
              <AlertDescription className="space-y-2 text-sm text-foreground">
                <p className="font-medium">Previously linked Encompass user no longer exists</p>
                <p>
                  This profile was linked to Encompass user{' '}
                  <code className="rounded bg-background px-1.5 py-0.5 font-mono text-xs">
                    {encompassUserId}
                  </code>
                  , but Encompass no longer has that user. Link an existing user, create a
                  replacement, or remove the stale link.
                </p>
              </AlertDescription>
            </Alert>

            <div className="lw-credential-card__actions flex flex-wrap items-center gap-2">
              <Button
                type="button"
                onClick={openConnectDialog}
                disabled={syncing}
                className="gap-2"
              >
                {isAdminView ? <Link2 className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
                {isAdminView ? 'Link or create replacement' : 'Create replacement user'}
              </Button>

              {isAdminView && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setUnlinkDialogOpen(true)}
                  disabled={syncing}
                  className={cn(
                    'gap-2 text-red-600 hover:bg-red-50 hover:text-red-700',
                    isProfilePresentation && 'lw-credential-card__action--danger'
                  )}
                >
                  <Unlink className="h-4 w-4" />
                  Remove stale link…
                </Button>
              )}
            </div>
          </div>
        ) : encompassUserId ? (
          <div className="space-y-4">
            {linkState === 'verification_unavailable' && (
              <Alert className="border-warning/40 bg-warning/10" role="status">
                <AlertCircle className="h-4 w-4 text-warning" aria-hidden="true" />
                <AlertDescription className="text-sm text-foreground">
                  {verificationMessage || 'The saved Encompass link could not be verified right now.'}
                </AlertDescription>
              </Alert>
            )}

            <Card className={cn(
              linkState === 'verification_unavailable'
                ? 'border-warning/40 bg-warning/10'
                : 'bg-green-50 border-green-200',
              isProfilePresentation && 'lw-credential-card__status-panel'
            )}>
              <CardContent className={cn('pt-6', isProfilePresentation && 'lw-credential-card__status-content')}>
                <div className={cn('flex items-start justify-between mb-4', isProfilePresentation && 'lw-credential-card__state-heading')}>
                  <div className="flex items-center gap-2">
                    {linkState === 'verification_unavailable' ? (
                      <AlertCircle className="h-5 w-5 text-warning" />
                    ) : (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    )}
                    <span className="font-medium text-green-900">
                      {linkState === 'verification_unavailable'
                        ? 'Encompass link verification unavailable'
                        : 'Connected to Encompass'}
                    </span>
                  </div>
                </div>

                <div className="lw-credential-card__kv-list space-y-3">
                  <div className="lw-credential-card__kv-row flex items-center justify-between">
                    <Label className="text-green-800">Encompass User ID</Label>
                    <div className="flex items-center gap-2">
                      <code className="lw-credential-card__mono px-2 py-1 bg-white rounded text-sm font-mono text-green-900">
                        {encompassUserId}
                      </code>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="Copy Encompass user ID"
                        onClick={copyEncompassId}
                        className="h-8 w-8 hover:bg-green-100"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {syncedAt && (
                    <div className="lw-credential-card__kv-row flex items-center justify-between text-sm">
                      <span className="text-green-800">Last synced</span>
                      <span className="text-green-900">{syncedAt.toLocaleString()}</span>
                    </div>
                  )}
                </div>

                {temporaryPassword && (
                  <Alert className="lw-credential-card__temporary-password mt-4 bg-yellow-50 border-yellow-200">
                    <Info className="h-4 w-4 text-yellow-600" />
                    <AlertDescription className="text-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <strong>Temporary Password:</strong>
                        <code className="px-2 py-1 bg-yellow-100 rounded font-mono">{temporaryPassword}</code>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label="Copy temporary Encompass password"
                          onClick={copyTemporaryPassword}
                          className="h-7 w-7 hover:bg-yellow-100"
                        >
                          <Copy className="h-3.5 w-3.5 text-yellow-700" />
                        </Button>
                      </div>
                      <span className="text-xs text-muted-foreground block">
                        User must change this password on first login to Encompass
                      </span>
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            <div className="lw-credential-card__actions flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleConnect}
                disabled={syncing}
                className="gap-2"
              >
                {syncing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <RefreshCcw className="h-4 w-4" />
                    {isProfilePresentation ? 'Update profile' : 'Update Profile'}
                  </>
                )}
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={() => setResetPasswordDialogOpen(true)}
                disabled={syncing}
                className={cn(
                  'gap-2',
                  !isProfilePresentation
                    && 'text-orange-600 hover:text-orange-700 hover:bg-orange-50 border-orange-200'
                )}
              >
                <Key className="h-4 w-4" />
                {isProfilePresentation ? 'Reset password' : 'Reset Password'}
              </Button>

              {isAdminView && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setUnlinkDialogOpen(true)}
                  disabled={syncing}
                  className={cn(
                    'gap-2 text-red-600 hover:text-red-700 hover:bg-red-50',
                    isProfilePresentation && 'lw-credential-card__action--danger'
                  )}
                >
                  <Unlink className="h-4 w-4" />
                  {isProfilePresentation ? 'Disable & unlink…' : 'Disable & Unlink'}
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {syncError && (
              <Alert variant="destructive" className="bg-red-50 border-red-200">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  {syncError}
                </AlertDescription>
              </Alert>
            )}

            <Card className={cn('bg-gray-50 border-gray-200', isProfilePresentation && 'lw-credential-card__empty-panel')}>
              <CardContent className="pt-6">
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="p-3 bg-gray-100 rounded-full">
                    <Users className="h-8 w-8 text-gray-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">Not Connected to Encompass</h4>
                    <p className="text-sm text-gray-600 max-w-sm">
                      Connect this profile to enable loan origination features and sync user data with Encompass.
                    </p>
                  </div>
                  <Button
                    type="button"
                    onClick={openConnectDialog}
                    disabled={syncing}
                    className="gap-2"
                    size="lg"
                  >
                    <Link2 className="h-5 w-5" />
                    Connect to Encompass
                  </Button>
                </div>
              </CardContent>
            </Card>

            {!isProfilePresentation && (
              <div className="bg-muted/50 rounded-md p-4 space-y-2">
                <p className="text-xs font-medium text-muted-foreground">What happens when you connect:</p>
                <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                  {isAdminView ? (
                    <>
                      <li>Searches for existing Encompass user by email ({userEmail})</li>
                      <li>Allows you to select and link an existing user</li>
                      <li>Or creates a new Encompass user with auto-generated credentials</li>
                    </>
                  ) : (
                    <li>Creates a new Encompass user with auto-generated credentials</li>
                  )}
                  <li>Enables automatic profile sync between systems</li>
                  <li>Unlocks loan origination features</li>
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {connectDialogOpen && (
        <LwModal
          className="lw-credential-dialog lw-credential-dialog--connect"
          containerClassName="lw-credential-modal"
          size="wide"
          title={<>Connect to <em>Encompass</em></>}
          sub={
            linkState === 'missing'
              ? isAdminView
                ? 'The previous Encompass user no longer exists. Link an existing user or create a replacement.'
                : 'The previous Encompass user no longer exists. Create a replacement for this profile.'
              : isAdminView
                ? 'Select an existing Encompass user to link this profile to, or create a new Encompass user.'
                : 'Create a new Encompass user for this profile.'
          }
          onClose={() => setConnectDialogOpen(false)}
          footer={(
            <>
              <LwButton
                variant="sm"
                onClick={() => {
                  setConnectDialogOpen(false);
                  setSearchResults([]);
                  setSyncedUserCount(0);
                  setSelectedUserId('');
                }}
                disabled={syncing}
              >
                Cancel
              </LwButton>
              <LwButton
                variant="sm-solid"
                onClick={handleConnect}
                disabled={syncing || (connectionMethod === 'search' && searchResults.length > 0 && !selectedUserId)}
              >
                {syncing ? (
                  <>
                    <Loader2 className="animate-spin" />
                    Connecting...
                  </>
                ) : connectionMethod === 'create' ? (
                  <>
                    <UserPlus />
                    Create &amp; Connect
                  </>
                ) : selectedUserId ? (
                  <>
                    <Link2 />
                    Link Selected User
                  </>
                ) : (
                  <>
                    <Link2 />
                    Select User
                  </>
                )}
              </LwButton>
            </>
          )}
        >
          <RadioGroup
            className="lw-credential-dialog__radio-group"
            aria-label="Encompass connection method"
            value={connectionMethod === 'create' ? 'create' : selectedUserId}
            onValueChange={(value) => {
              if (value === 'create') {
                setConnectionMethod('create');
                return;
              }
              setSelectedUserId(value);
              setConnectionMethod('search');
            }}
          >
            {isAdminView && (
              searching ? (
                <div className="lw-credential-dialog__loading" role="status">
                  <Loader2 className="animate-spin" />
                  <span>Loading available Encompass users...</span>
                </div>
              ) : searchResults.length > 0 ? (
                <>
                  <div className="lw-credential-dialog__section-heading">
                    <span>Existing Encompass Users</span>
                    <span className="lw-credential-dialog__count">
                      {syncedUserCount} already synced
                    </span>
                  </div>
                  <div className="lw-credential-dialog__choices">
                    {searchResults.map((user) => {
                      const optionId = `encompass-user-${user.id}`;
                      const displayName = user.fullName
                        || `${user.firstName || ''} ${user.lastName || ''}`.trim()
                        || user.id;
                      return (
                        <div
                          key={user.id}
                          className="lw-credential-dialog__choice"
                          data-selected={selectedUserId === user.id}
                          onClick={() => {
                            setSelectedUserId(user.id);
                            setConnectionMethod('search');
                          }}
                        >
                          <RadioGroupItem value={user.id} id={optionId} />
                          <Label htmlFor={optionId} className="lw-credential-dialog__choice-label">
                            <span className="lw-credential-dialog__choice-copy">
                              <span className="lw-credential-dialog__choice-title">{displayName}</span>
                              <span className="lw-credential-dialog__choice-meta">
                                ID: {user.id} · {user.email || 'No email'}
                                {user.personaNames ? <> · <strong>Role:</strong> {user.personaNames}</> : null}
                              </span>
                            </span>
                            <LwStatusPill
                              label={user.loginEnabled ? 'Active' : 'Inactive'}
                              tone={user.loginEnabled ? 'success' : 'neutral'}
                            />
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                  <div className="lw-credential-dialog__divider" aria-hidden="true">
                    <span>OR</span>
                  </div>
                </>
              ) : (
                <div className="lw-credential-dialog__notice" data-tone="info">
                  <Info aria-hidden="true" />
                  <p>
                    No existing Encompass users are available. Create a new Encompass user for{' '}
                    <strong>{firstName} {lastName}</strong> below.
                  </p>
                </div>
              )
            )}

            <div
              className="lw-credential-dialog__choice lw-credential-dialog__choice--create"
              data-selected={connectionMethod === 'create'}
              onClick={() => setConnectionMethod('create')}
            >
              <RadioGroupItem value="create" id="create-new-encompass-user" />
              <Label
                htmlFor="create-new-encompass-user"
                className="lw-credential-dialog__choice-label"
              >
                <UserPlus className="lw-credential-dialog__choice-icon" aria-hidden="true" />
                <span className="lw-credential-dialog__choice-copy">
                  <span className="lw-credential-dialog__choice-title">
                    Create New Encompass User
                  </span>
                  <span className="lw-credential-dialog__choice-meta">
                    Will create{' '}
                    <code>
                      {firstName?.charAt(0).toLowerCase()}
                      {lastName?.toLowerCase().replace(/[^a-z0-9]/g, '') || 'username'}
                    </code>{' '}
                    for {firstName} {lastName}
                  </span>
                </span>
              </Label>

              {connectionMethod === 'create' && (
                <div className="lw-credential-dialog__create-fields">
                  {loadingSettings ? (
                    <div className="lw-credential-dialog__loading" role="status">
                      <Loader2 className="animate-spin" />
                      <span>Loading organization settings...</span>
                    </div>
                  ) : (
                    <>
                      <div className="lw-credential-dialog__field">
                        <Label htmlFor="organization">Organization</Label>
                        <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
                          <SelectTrigger id="organization">
                            <SelectValue placeholder="Select organization..." />
                          </SelectTrigger>
                          <SelectContent className={`${LW_DS_CLASS} lw-credential-select`}>
                            {organizations.map((org) => (
                              <SelectItem key={org.id} value={org.id}>
                                {org.path}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p>Which Encompass organization to place the user in</p>
                      </div>

                      <div className="lw-credential-dialog__field">
                        <Label htmlFor="persona">Persona</Label>
                        <Select value={selectedPersonaId} onValueChange={setSelectedPersonaId}>
                          <SelectTrigger id="persona">
                            <SelectValue placeholder="Select persona..." />
                          </SelectTrigger>
                          <SelectContent className={`${LW_DS_CLASS} lw-credential-select`}>
                            {personas.map((persona) => (
                              <SelectItem key={persona.id} value={persona.id}>
                                {persona.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p>Assign the user's role/permissions in Encompass</p>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </RadioGroup>
        </LwModal>
      )}

      {unlinkDialogOpen && (
        <LwModal
          className="lw-credential-dialog lw-credential-dialog--destructive"
          containerClassName="lw-credential-modal"
          size="wide"
          title={(
            <span className="lw-credential-dialog__danger-title">
              {linkState === 'missing' ? 'Remove Stale Encompass Link' : 'Disable & Unlink Encompass User'}
            </span>
          )}
          sub={
            linkState === 'missing'
              ? 'The previously linked Encompass user no longer exists. Remove the stale connection from this LendWise profile.'
              : 'Unlink this Encompass user? This disables the Encompass account, detaches its email, and removes the connection to this LendWise profile.'
          }
          onClose={() => setUnlinkDialogOpen(false)}
          footer={(
            <>
              <LwButton
                variant="sm"
                onClick={() => setUnlinkDialogOpen(false)}
                disabled={syncing}
              >
                Cancel
              </LwButton>
              <LwButton
                variant="sm-danger"
                onClick={handleUnlink}
                disabled={syncing}
              >
                {syncing ? (
                  <>
                    <Loader2 className="animate-spin" />
                    Unlinking...
                  </>
                ) : (
                  linkState === 'missing' ? 'Remove Stale Link' : 'Disable & Unlink User'
                )}
              </LwButton>
            </>
          )}
        >
          <div className="lw-credential-dialog__notice" data-tone="warning">
            <AlertCircle aria-hidden="true" />
            <p>
              {linkState === 'missing' ? (
                <>
                  Encompass already reports this user as missing. This removes only the stale
                  LendWise association so you can link or create a replacement.
                </>
              ) : (
                <>
                  This will <strong>not</strong> delete the user from Encompass. It disables the
                  old account and changes its email so it can't be auto-relinked by email.
                </>
              )}
            </p>
          </div>
        </LwModal>
      )}

      {resetPasswordDialogOpen && (
        <LwModal
          className="lw-credential-dialog lw-credential-dialog--reset"
          containerClassName="lw-credential-modal"
          size="wide"
          title="Reset Encompass Password"
          sub="Generate a new temporary password for this Encompass account. Use it to log into Encompass and set a permanent password."
          onClose={() => setResetPasswordDialogOpen(false)}
          footer={(
            <>
              <LwButton
                variant="sm"
                onClick={() => setResetPasswordDialogOpen(false)}
                disabled={syncing}
              >
                Cancel
              </LwButton>
              <LwButton
                variant="sm-solid"
                className="lw-credential-dialog__gold-action"
                onClick={handleResetPassword}
                disabled={syncing}
              >
                {syncing ? (
                  <>
                    <Loader2 className="animate-spin" />
                    Resetting...
                  </>
                ) : (
                  <>
                    <Key />
                    Reset Password
                  </>
                )}
              </LwButton>
            </>
          )}
        >
          <div className="lw-credential-dialog__notice" data-tone="warning">
            <AlertCircle aria-hidden="true" />
            <p>
              <strong>Important:</strong> the current password is invalidated immediately.
              The new temporary password is shown only once.
            </p>
          </div>
          {encompassUserId && (
            <div className="lw-credential-dialog__summary">
              <span>Encompass User ID</span>
              <code>{encompassUserId}</code>
            </div>
          )}
        </LwModal>
      )}
    </>
  );
};
